import Button from "@mui/material/Button";
import {
  CreateAlgoRequest,
  create_algo,
  update_algo,
  update_algos,
} from "../../lazertrader/lazerApi";
import { useMsal } from "@azure/msal-react";
import { useFormikContext } from "formik";
import { ConfigTabState } from "../types";
import { EXCHANGE } from "../../../Services/ExchangeSelectorContextProvider";
import { useCallback } from "react";
import { useNotification } from "../../lazertrader/contexts/notifications";
import { useListedAlgo } from "../../lazertrader/contexts/listAlgosContext";
import { queryClient } from "../../../api/queryClient";
import { useShimuraiSelection } from "../hooks";
import { tradeEndBeforeStart } from "./time/utils";
import { Behavior } from "../algoSpec";

export type CreateAlgoFn = (userInitials: string) => CreateAlgoRequest;

export const useSubmit = () => {
  const msal = useMsal();

  const { addNotification } = useNotification();
  const { setSelectedAlgos } = useListedAlgo();

  const submit = useCallback(
    (resetForm: () => void, request: CreateAlgoRequest, isUpdate: boolean) => {
      console.log("Submitting algo", request);
      const action = isUpdate ? update_algo : create_algo;

      try {
        setSelectedAlgos([]);
        resetForm();

        action(EXCHANGE, msal, request)
          .then((response) => {
            queryClient.invalidateQueries({
              queryKey: ["v1/algos"],
            });
            addNotification(response, "success");
          })
          .catch((error) => {
            addNotification(error.toString(), "error");
          });
      } catch (e) {
        addNotification("Something went wrong: " + e?.toString(), "error");
      }
    },
    [addNotification, msal, setSelectedAlgos]
  );

  return submit;
};

/**
 * Retune several algos in one PUT.
 *
 * Same shape as {@link useSubmit} — clear the selection and reset the form
 * before the request, so the desk is not left staring at a stale form while it
 * flies — but there is no create counterpart: a bulk call only ever edits algos
 * the hub already runs.
 */
export const useSubmitBulk = () => {
  const msal = useMsal();

  const { addNotification } = useNotification();
  const { setSelectedAlgos } = useListedAlgo();

  const submitBulk = useCallback(
    (resetForm: () => void, requests: CreateAlgoRequest[]) => {
      console.log("Submitting algos", requests);

      try {
        setSelectedAlgos([]);
        resetForm();

        update_algos(EXCHANGE, msal, requests)
          .then((response) => {
            queryClient.invalidateQueries({
              queryKey: ["v1/algos"],
            });
            addNotification(response, "success");
          })
          .catch((error) => {
            addNotification(error.toString(), "error");
          });
      } catch (e) {
        addNotification("Something went wrong: " + e?.toString(), "error");
      }
    },
    [addNotification, msal, setSelectedAlgos]
  );

  return submitBulk;
};

export const SubmitButton = () => {
  const { values } = useFormikContext<ConfigTabState>();
  const shimurai = useShimuraiSelection();
  // A bulk override is always an edit, even though it leaves `algo_id` unset —
  // the ids live on the selection, not the form.
  const isUpdate = Boolean(values.algo_id) || (shimurai?.bulk ?? false);
  const color = isUpdate ? "warning" : "success";
  const label = isUpdate ? "Update" : "Submit";

  // A bulk override has no trade window of its own to contradict — the fields
  // are disabled and blank wherever the selection disagreed.
  //
  // Same check as the red error under the window, so the button is never
  // disabled without a visible reason. A blank end means "until close", not
  // "now" — `dayjs(undefined)` would read it as now and disable Submit the
  // moment a future start was typed.
  //
  // A terminator also needs its behaviour picked; `TerminatorSwitch` reads
  // "Pick one" while it is missing.
  const terminatorBehaviorMissing =
    values.behavior === Behavior.TERMINATOR &&
    values.terminator_behavior === undefined;
  const disabled =
    !shimurai?.bulk &&
    ((tradeEndBeforeStart(values.start, values.end) ?? false) ||
      terminatorBehaviorMissing);

  return (
    <Button
      sx={{ flex: 1 }}
      color={color}
      type="submit"
      variant="contained"
      disabled={disabled}
    >
      {label}
    </Button>
  );
};
